import { useEffect, useCallback, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useAccountAlias } from "resources/account"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"
import { MODULE_TYPES, type DashboardModule } from "../modules/types"
import { useDeletePinnedWorkflow } from "connected-components/ai-teammate/hooks/use-delete-pinned-workflow"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { EMPTY_MODULES } from "../store/panels-slice"
import { log } from "helpers/logger"
import { warn } from "helpers/dev-logger"

/**
 * Hook that listens for module removal events and handles cleanup for AI artifacts
 */
export function useRemoveArtifactHandler(): void {
  const { mutateAsync: deletePinnedWorkflow } = useDeletePinnedWorkflow()
  const accountAlias = useAccountAlias()
  const queryClient = useQueryClient()
  const dashboardId = useDashboardId()
  const modules = useAppSelector((s) => s.dashboard.panels[dashboardId]?.modules ?? EMPTY_MODULES)

  // Keep a live ref to modules so the event handler always reads the latest
  // state without resubscribing on every module change (fixes subscription gap).
  const modulesRef = useRef(modules)
  useEffect(() => { modulesRef.current = modules }, [modules])

  const handleModuleRemoved = useCallback(async (eventModule: DashboardModule) => {
    if (eventModule.type !== MODULE_TYPES.AI_ARTIFACT) {
      return
    }

    const currentModule = modulesRef.current.find((m) => m.id === eventModule.id)
    const module = currentModule || eventModule
    const pinnedId = module.config?.pinnedId
    const isPinning = module.config?.isPinning

    if (pinnedId) {
      queryClient.removeQueries({ queryKey: ["ai-artifact", pinnedId] })

      try {
        await deletePinnedWorkflow({
          accountAlias,
          pinnedId,
        })
      } catch (error) {
        log(`Failed to delete pinned workflow: ${error}`)
      }
    }

    if (isPinning) {
      warn("AI artifact module removed while pin was in progress")
    }
  }, [deletePinnedWorkflow, accountAlias, queryClient])

  useEffect(() => {
    const cleanupFn = dashboardEvents.on<DashboardModule>(
      DASHBOARD_EVENTS.MODULE_REMOVED,
      handleModuleRemoved,
    )

    return cleanupFn
  }, [handleModuleRemoved])
}
